import { API_URL } from '../services/api';
import { log } from './logger';

/**
 * Rete di sicurezza per quando l'aggiornamento via service worker non può
 * funzionare.
 *
 * ## Perché esiste
 *
 * `useAppUpdate` si accorge di una versione nuova in un modo solo: chiedendo al
 * browser di ri-scaricare `/sw.js`. Quella richiesta — e i `fetch()` che workbox
 * fa dentro `install` per riempire il precache — non ha un browsing context,
 * quindi il browser **non può mostrare una finestra di autenticazione** e dipende
 * interamente dalla cache HTTP delle credenziali, che vive in memoria e muore col
 * processo.
 *
 * Su un host che mette la parte statica dietro basic auth — è il caso del droplet
 * di prova — questo basta a spegnere l'aggiornamento per sempre: una PWA
 * offline-first, al lancio a freddo, serve la home dal precache SENZA toccare la
 * rete, quindi nessuna autenticazione avviene, e il primo `update()` prende un 401
 * con `content-type: text/html` — che fallisce sia il requisito 2xx sia il
 * controllo del MIME type per uno script di service worker. Nessun worker arriva
 * mai in attesa, quindi non scatta nemmeno il ripiego «si attiva al prossimo
 * avvio pulito»: non c'è niente da attivare. L'app continua a funzionare, sul
 * bundle vecchio, all'infinito.
 *
 * `/api/version` invece si raggiunge, perché l'API non sta dietro lo stesso gate.
 * Era la strada usata fino al 7 agosto 2026, quando `5b77c70e` ha sostituito il
 * poll con `needRefresh` e l'ha tolta: da lì lo staging ha smesso di aggiornarsi.
 * Qui torna, ma come RIPIEGO del percorso service worker e non come suo
 * sostituto — su un host sano il predicato qui sotto resta falso e questo codice
 * non fa mai niente.
 */

/**
 * Quante letture di fila devono trovare la versione diversa prima di agire.
 *
 * Una sola non basta: durante un deploy il server può già servire la versione
 * nuova mentre il worker nuovo è ancora in viaggio, ed è esattamente la finestra
 * in cui il percorso normale sta per funzionare da solo. La seconda lettura
 * arriva dopo `RICONTROLLO_RAPIDO_MS`, non dopo il throttle ordinario.
 */
export const MISMATCH_PRIMA_DI_AGIRE = 2;

/**
 * Quanto si aspetta prima della SECONDA lettura, dopo che la prima ha trovato
 * una versione diversa.
 *
 * Il throttle ordinario dei controlli e' dieci minuti, ma qui sarebbe tempo
 * buttato: le due letture servono a non agire MENTRE un deploy e' in corso, e un
 * deploy dura molto meno. Per di piu' la corsa in pratica non esiste — deploy.sh
 * scrive `packages/web/dist` al passo 5 e riavvia PM2 solo al passo 6, quindi
 * quando `/api/version` annuncia la versione nuova il `sw.js` nuovo e' gia' sul
 * disco. Cio' che protegge davvero la produzione non e' l'attesa, sono
 * `workerInArrivo` e `updateInCorso` qui sotto.
 */
export const RICONTROLLO_RAPIDO_MS = 60 * 1000;

/**
 * Le cache che il recupero NON deve toccare: sono i `runtimeCaching` dichiarati
 * in vite.config.ts, che non c'entrano niente col bundle.
 *
 * L'elenco è per esclusione, e di proposito: ciò che il recupero DEVE cancellare
 * è il precache, e il suo nome lo decide workbox (`workbox-precache-v2-…`). Una
 * lista di cose da cancellare smetterebbe in silenzio di funzionare il giorno in
 * cui quel nome cambia, e il recupero ricaricherebbe lo stesso identico bundle.
 *
 * ⚠️ Va tenuto allineato a `runtimeCaching` in vite.config.ts. `board-textures`
 * sono 1,2 MB di venature che si riscaricherebbero per niente.
 */
const CACHE_DA_CONSERVARE = new Set(['board-textures', 'google-fonts-cache']);

export interface StatoVersione {
  /** La versione cotta nel bundle in esecuzione (`__APP_VERSION__`). */
  appVersion: string;
  /** Quella dichiarata da `GET /api/version`, o `null` se la sonda non risponde. */
  serverVersion: string | null;
  /** Quante letture consecutive hanno trovato le due diverse. */
  mismatchConsecutivi: number;
  /**
   * Un worker nuovo è in attesa **o si sta installando**, o `needRefresh` è già
   * vero. `installing` conta quanto `waiting`: precaricare 2,8 MB può durare piu'
   * del ricontrollo rapido, e un'installazione in corso è la prova che il
   * percorso normale sta funzionando.
   */
  workerInArrivo: boolean;
  /**
   * C'è un `update()` ancora in volo. Su una rete lentissima anche lo scaricamento
   * di `sw.js` può non essere finito: finché quella richiesta non ha detto come è
   * andata, non si sa niente e non si tocca niente.
   */
  updateInCorso: boolean;
}

/**
 * Il percorso service worker è DIMOSTRABILMENTE bloccato?
 *
 * Deliberatamente severo: è l'unica cosa che autorizza un recupero distruttivo,
 * e un falso positivo costa all'utente la cancellazione del precache.
 */
export function percorsoWorkerBloccato(stato: StatoVersione): boolean {
  // Se un worker sta arrivando il percorso normale funziona, e ha la precedenza:
  // sa passare alla versione nuova senza buttare via niente.
  if (stato.workerInArrivo) return false;
  if (stato.updateInCorso) return false;
  if (stato.serverVersion === null) return false;
  if (stato.serverVersion === stato.appVersion) return false;
  return stato.mismatchConsecutivi >= MISMATCH_PRIMA_DI_AGIRE;
}

/**
 * Chiede al server che versione sta servendo. `null` per qualunque intoppo —
 * rete assente, 500, corpo inatteso: nessuno di questi è la situazione che
 * stiamo cercando, e trattarli come tale innescherebbe il recupero proprio
 * quando la rete balla.
 */
export async function leggiVersioneServer(): Promise<string | null> {
  try {
    const risposta = await fetch(`${API_URL}/version`);
    if (!risposta.ok) return null;
    const corpo = (await risposta.json()) as { version?: string } | null;
    return corpo?.version ?? null;
  } catch {
    return null;
  }
}

/**
 * Ricarica l'app scavalcando il precache.
 *
 * `window.location.reload()` da solo NON basta, per la stessa ragione scritta in
 * `ricarica-app.ts`: col worker vecchio ancora al comando il ricarico gli
 * richiede le stesse risorse precaricate e rimette in piedi lo stesso identico
 * bundle. E qui, per ipotesi, un worker nuovo non esiste — quindi non c'è nemmeno
 * un `SKIP_WAITING` da mandargli.
 *
 * Si toglie di mezzo il worker e il suo precache: al ricarico la pagina non è
 * controllata da nessuno, la navigazione va in rete DENTRO un browsing context —
 * dove il browser può finalmente chiedere la password, se serve — e ne escono
 * HTML e asset nuovi. Il service worker si ri-registra dal `sw.js` nuovo.
 */
export async function recuperaScavalcandoIlPrecache(): Promise<void> {
  try {
    const registrazione = await navigator.serviceWorker?.getRegistration();
    await registrazione?.unregister();
  } catch (err) {
    // Proseguire comunque: senza le cache, il worker vecchio non ha più nulla da
    // servire e il ricarico va in rete lo stesso.
    log.warn('ControlloVersione', 'unregister fallito, proseguo', { errore: (err as Error)?.message });
  }

  try {
    const nomi = await caches.keys();
    await Promise.all(nomi.filter((n) => !CACHE_DA_CONSERVARE.has(n)).map((n) => caches.delete(n)));
  } catch (err) {
    log.warn('ControlloVersione', 'pulizia delle cache fallita', { errore: (err as Error)?.message });
  }
}
